<script setup lang="ts">
/**
 * Left cell for the mobile clients table.
 *
 * Renders a two-line block: row index (small, right-aligned, muted) to the
 * left of the name stack. The name stack has the primary name on top and a
 * secondary meta line (gender letter, registration duration) below.
 */
import { computed } from 'vue'
import { formatName } from '@/utils/formatName'
import { formatStartDate } from '@/utils/formatStartDate'

const props = withDefaults(
  defineProps<{
    index?: number
    name?: string
    gender?: string | null
    startDate?: Date | string
    width?: string
  }>(),
  {},
)

const formattedName = computed(() => formatName(props.name))

const genderLabel = computed(() => {
  const raw = props.gender
  if (typeof raw !== 'string' || raw.trim() === '') return null
  const ch = raw.trim().charAt(0).toLowerCase()
  return ch === 'm' || ch === 'f' ? ch : null
})

const hasStartDate = computed(
  () =>
    props.startDate !== undefined &&
    props.startDate !== null &&
    (typeof props.startDate !== 'string' || props.startDate.trim() !== ''),
)

const durationSince = computed(() =>
  hasStartDate.value ? formatStartDate({ date: props.startDate! }).durationSince : '',
)

const metaText = computed(() => {
  const g = genderLabel.value
  const d = durationSince.value
  if (g && d) return `${g}, ${d}`
  return g ?? d ?? ''
})
</script>

<template>
  <td class="client-mobile-left font-tufte text-tufte-text">
    <div class="row-layout">
      <span class="row-idx text-tufte-muted font-tufte-sans tabular-nums">{{ index }}</span>
      <div class="row-main">
        <span class="row-name">{{ formattedName }}</span>
        <span v-if="metaText" class="row-meta text-tufte-muted">{{ metaText }}</span>
      </div>
    </div>
  </td>
</template>

<style scoped>
.row-layout {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
}

.row-idx {
  width: 1.75rem;
  flex-shrink: 0;
  text-align: right;
  font-size: var(--text-tufte-xs);
  padding-top: 0.25rem;
}

.row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.row-name {
  font-size: var(--text-tufte-lg);
  font-weight: 500;
  line-height: 1.2;
}

.row-meta {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  line-height: var(--line-height-tufte-normal);
  white-space: nowrap;
}
</style>
